import { useTranslate } from "@refinedev/core";
import { Flex, Popover, Spin, Typography } from "antd";
import { ReactElement, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  FaceProfileCreationResponse,
  FaceProfileResponse,
  VoiceProfileCreationResponse,
  VoiceProfileResponse,
} from "../../types";
import { AVATAR_ITEM_PREVIEW_BORDER_RADIUS } from "./AvatarDetails";
import { AvatarFacePreview } from "./AvatarFacePreview";
import { getFaceProfileImagePath, getProfileIdField } from "./helpers";
import { PaginatedList } from "./PaginatedList";
import { SelectedItem } from "./styled";

type InfiniteProfileListProps<
  T extends FaceProfileResponse | VoiceProfileResponse,
  J extends FaceProfileCreationResponse | VoiceProfileCreationResponse,
> = {
  profiles: T[];
  isLoading: boolean;
  selected?: T;
  clonedResponse?: J;
  setCloned: (cloned: J | undefined) => void;
  setSelected: (profile: T | undefined) => void;
  cloneButton?: ReactElement;
  type: "face" | "voice";
  hasMore?: boolean;
  loadMoreData?: () => void;
  renderItem?: (
    item: T,
    index: number,
    setSelected: (profile: T) => void,
    selectedItem?: T,
    cloned?: J,
    setCloned?: (cloned: J | undefined) => void
  ) => JSX.Element;
  isNewAvatar?: boolean;
};

export const InfiniteProfileList = <
  T extends FaceProfileResponse | VoiceProfileResponse,
  J extends FaceProfileCreationResponse | VoiceProfileCreationResponse,
>({
  profiles,
  isLoading,
  selected,
  clonedResponse,
  setCloned,
  setSelected,
  cloneButton,
  type,
  hasMore,
  loadMoreData,
  renderItem,
  isNewAvatar,
}: InfiniteProfileListProps<T, J>) => {
  const t = useTranslate();

  const { id } = useParams();

  const displayItems = profiles;
  const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);

  const columns = useMemo(
    () => (!id || isNewAvatar ? 4 : 3),
    [id, isNewAvatar]
  );
  const itemsPerPage = columns;
  const totalPages = Math.ceil(displayItems.length / itemsPerPage);

  const handlePageChange = (firstItemIndex: number) => {
    const newPage = Math.ceil((firstItemIndex + itemsPerPage) / itemsPerPage);
    // Load more when we're on the last page or the one before last
    const shouldLoad = newPage >= totalPages - 1;

    if (hasMore && loadMoreData && !isLoading && shouldLoad) {
      loadMoreData();
    }
  };

  // Check on initial load if we have less than 2 slides worth of items
  useEffect(() => {
    if (
      displayItems.length > 0 &&
      displayItems.length < itemsPerPage * 2 &&
      hasMore &&
      loadMoreData &&
      !isLoading
    ) {
      loadMoreData();
    }
  }, [displayItems.length, itemsPerPage, hasMore, loadMoreData, isLoading]);

  return (
    <Flex vertical style={{ width: "100%" }}>
      {cloneButton && (
        <Flex
          vertical
          justify="center"
          align="center"
          gap={16}
          style={{ width: "100%" }}
        >
          <Typography.Text
            style={{
              display: "block",
              textAlign: "center",
              marginTop: 10,
            }}
          >
            {t(
              `components.ContactWithPresenter.${capitalizedType}Selector.no${capitalizedType}sFound`
            )}
          </Typography.Text>
          {cloneButton}
        </Flex>
      )}
      {/* initial data loading */}
      {isLoading && profiles.length === 0 ? (
        <Flex
          align="center"
          justify="center"
          style={{ height: "100%", padding: 20 }}
        >
          <Spin size="large" />
        </Flex>
      ) : (
        <PaginatedList
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          loadingMore={isLoading && profiles.length > 0}
          style={{
            padding: "0 24px",
            width: "100%",
            height: "auto",
          }}
        >
          {displayItems.map((item, index) => {
            const field = getProfileIdField(item);
            const clonedId = field
              ? clonedResponse?.[field as keyof J]
              : undefined;
            const itemSelected = (selected?.id ?? clonedId) === item.id;
            const imagePath = getFaceProfileImagePath(item);
            return (
              <div key={item.id}>
                {type === "face" ? (
                  <Popover
                    mouseEnterDelay={0.7}
                    placement="right"
                    autoAdjustOverflow
                    content={
                      imagePath && (
                        <div
                          style={{
                            backgroundImage:
                              "linear-gradient(0deg, #999 0%, #eee 100%)",
                            overflow: "hidden",
                            height: 300,
                          }}
                        >
                          <img
                            src={imagePath}
                            loading="lazy"
                            style={{
                              bottom: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              marginBottom: -20,
                            }}
                            alt={item.name}
                          />
                        </div>
                      )
                    }
                  >
                    <div
                      style={{
                        paddingTop: 5,
                        paddingRight: 5,
                        maxWidth: 320,
                      }}
                    >
                      <StyledFlex
                        className={itemSelected ? "selected" : ""}
                        style={{
                          width: "100%",
                          borderRadius: AVATAR_ITEM_PREVIEW_BORDER_RADIUS,
                        }}
                      >
                        <AvatarFacePreview
                          id={item.id}
                          assetPath={imagePath ?? undefined}
                          name={item.name}
                          onClick={() => {
                            setCloned(undefined);
                            if (item.id === selected?.id) {
                              setSelected(undefined);
                            } else {
                              setSelected(item);
                            }
                          }}
                          ghost={!imagePath}
                        />
                      </StyledFlex>
                    </div>
                  </Popover>
                ) : (
                  renderItem?.(
                    item,
                    index,
                    setSelected,
                    selected,
                    clonedResponse,
                    setCloned
                  )
                )}
              </div>
            );
          })}
        </PaginatedList>
      )}
    </Flex>
  );
};

const StyledFlex = SelectedItem(Flex);
